import { useEffect } from "react";
import * as THREE from "three";
import { CSS2DObject } from "three-stdlib";
import { useMeasurementToolState, useStaticInterferenceState } from "../../stores/measurementStore";
import { disposeSceneObjectTree } from "../scene/sceneDispose";
import { safeTraverse } from "../scene/sceneObjectLookups";

const MEASUREMENT_GROUP_NAME = "__measurementOverlays";
const POINT_COLOR = 0xfacc15;
const DISTANCE_COLOR = 0x38bdf8;
const ANGLE_COLOR = 0xa78bfa;
const INTERFERENCE_COLOR = 0xff4d4d;

function clearGroup(group) {
  if (!group) return;
  while (group.children.length > 0) {
    const child = group.children[0];
    group.remove(child);
    disposeSceneObjectTree(child);
  }
}

function createLabel(text, position, className = "") {
  const div = document.createElement("div");
  div.className = [
    "rounded border border-gray-700 bg-gray-950/90 px-2 py-1 font-mono text-[11px] text-gray-100 shadow-lg",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  div.textContent = text;
  const label = new CSS2DObject(div);
  label.position.copy(position);
  label.userData = { isMeasurementOverlay: true, isManagedLabel: true };
  return label;
}

function createPointMarker(point, color = POINT_COLOR) {
  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(0.018, 16, 16),
    new THREE.MeshBasicMaterial({
      color,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    })
  );
  marker.position.set(point.position[0], point.position[1], point.position[2]);
  marker.renderOrder = 10030;
  marker.userData = { isMeasurementOverlay: true };
  return marker;
}

function createLine(points, color) {
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const line = new THREE.Line(
    geometry,
    new THREE.LineBasicMaterial({
      color,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    })
  );
  line.renderOrder = 10029;
  line.userData = { isMeasurementOverlay: true };
  return line;
}

function midpoint(a, b) {
  return new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
}

function pointToVector(point) {
  return new THREE.Vector3(point.position[0], point.position[1], point.position[2]);
}

function formatMeters(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0 m";
  return `${n.toFixed(n < 0.01 ? 5 : 4)} m`;
}

function formatDegrees(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0 deg";
  return `${n.toFixed(2)} deg`;
}

function addMeasurement(group, measurement) {
  const vectors = measurement.points.map(pointToVector);
  measurement.points.forEach((point) => group.add(createPointMarker(point)));
  if (measurement.type === "distance") {
    group.add(createLine(vectors, DISTANCE_COLOR));
    group.add(createLabel(formatMeters(measurement.meters), midpoint(vectors[0], vectors[1])));
    return;
  }
  group.add(createLine([vectors[0], vectors[1], vectors[2]], ANGLE_COLOR));
  group.add(createLabel(formatDegrees(measurement.degrees), vectors[1].clone()));
}

function addCurrentPoints(group, currentPoints) {
  currentPoints.forEach((point) => group.add(createPointMarker(point, POINT_COLOR)));
  if (currentPoints.length > 1) {
    group.add(createLine(currentPoints.map(pointToVector), POINT_COLOR));
  }
}

function findBodyObjects(scene, bodyUuids) {
  const bodySet = new Set(bodyUuids.filter(Boolean).map(String));
  const objects = [];
  if (bodySet.size === 0) return objects;
  safeTraverse(scene, (obj) => {
    const bodyUuid = obj?.userData?.bodyUuid;
    if (!bodyUuid || !bodySet.has(String(bodyUuid))) return;
    if (obj.isMesh) objects.push(obj);
  });
  return objects;
}

function addInterferenceHighlights(group, scene, contacts) {
  const bodyUuids = [];
  contacts.forEach((contact) => {
    if (contact.body1Uuid) bodyUuids.push(contact.body1Uuid);
    if (contact.body2Uuid) bodyUuids.push(contact.body2Uuid);
    if (Array.isArray(contact.position)) {
      group.add(
        createPointMarker(
          {
            position: contact.position,
          },
          INTERFERENCE_COLOR
        )
      );
    }
  });

  findBodyObjects(scene, bodyUuids).forEach((obj) => {
    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) return;
    const helper = new THREE.Box3Helper(box, INTERFERENCE_COLOR);
    helper.renderOrder = 10028;
    helper.userData = { isMeasurementOverlay: true };
    group.add(helper);
  });
}

export function useMeasurementOverlays({ sceneRef, structureChangeTick }) {
  const { currentPoints, measurements } = useMeasurementToolState();
  const { result: staticInterferenceResult } = useStaticInterferenceState();

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return undefined;

    let group = scene.getObjectByName(MEASUREMENT_GROUP_NAME);
    if (!group) {
      group = new THREE.Group();
      group.name = MEASUREMENT_GROUP_NAME;
      group.renderOrder = 10027;
      group.userData = { isMeasurementOverlay: true };
      scene.add(group);
    }

    clearGroup(group);
    measurements.forEach((measurement) => addMeasurement(group, measurement));
    addCurrentPoints(group, currentPoints);
    if (staticInterferenceResult?.contacts?.length) {
      addInterferenceHighlights(group, scene, staticInterferenceResult.contacts);
    }

    if (
      group.children.length === 0 &&
      !measurements.length &&
      !currentPoints.length &&
      !staticInterferenceResult?.contacts?.length
    ) {
      group.parent?.remove(group);
    }

    return () => {
      clearGroup(group);
      group.parent?.remove(group);
    };
  }, [currentPoints, measurements, sceneRef, staticInterferenceResult, structureChangeTick]);
}
